import { useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ArrowRight, Flame, Dumbbell, HeartPulse, Leaf, Wind, Target, Timer } from "lucide-react";
import { useReveal } from "@/lib/useReveal";

const diffBadge = {
  Easy: "bg-[#2EE59D]/15 text-[#2EE59D] border-[#2EE59D]/25",
  Medium: "bg-neon-cyan/15 text-neon-cyan border-neon-cyan/25",
  Hard: "bg-neon-orange/15 text-neon-orange border-neon-orange/25",
};

const iconForCat = {
  HIIT: Flame,
  Strength: Dumbbell,
  Cardio: HeartPulse,
  Yoga: Leaf,
  Mobility: Wind,
  Core: Target,
};

const workouts = [
  {
    id: "w1",
    name: "Neon Burn HIIT",
    category: "HIIT",
    difficulty: "Hard",
    duration: 28,
    equipment: "Bodyweight",
    kcal: 420,
    progress: 62,
    accent: "lime",
    copy: "Max-effort intervals built to torch fat without killing recovery.",
  },
  {
    id: "w2",
    name: "Push Day PR Builder",
    category: "Strength",
    difficulty: "Medium",
    duration: 55,
    equipment: "Barbell, Dumbbells",
    kcal: 380,
    progress: 45,
    accent: "cyan",
    copy: "Bench + shoulders progression tuned to your recent PRs.",
  },
  {
    id: "w3",
    name: "Zone 2 Endurance",
    category: "Cardio",
    difficulty: "Easy",
    duration: 40,
    equipment: "Bike or Run",
    kcal: 310,
    progress: 80,
    accent: "cyan",
    copy: "Heart-rate capped session to build aerobic base without fatigue.",
  },
  {
    id: "w4",
    name: "Flow & Recover Yoga",
    category: "Yoga",
    difficulty: "Easy",
    duration: 30,
    equipment: "Mat",
    kcal: 150,
    progress: 28,
    accent: "lime",
    copy: "Dynamic flow + deep stretches to prime nervous system recovery.",
  },
  {
    id: "w5",
    name: "Mobility Reset",
    category: "Mobility",
    difficulty: "Easy",
    duration: 18,
    equipment: "Foam Roller",
    kcal: 90,
    progress: 52,
    accent: "cyan",
    copy: "Hips, thoracic, and ankles — everything lifting steals from you.",
  },
  {
    id: "w6",
    name: "Core Finisher — 6 Min",
    category: "Core",
    difficulty: "Hard",
    duration: 6,
    equipment: "Bodyweight",
    kcal: 70,
    progress: 74,
    accent: "orange",
    copy: "Tri-set torture: rectus, obliques, transverse — no rest.",
  },
];

const categories = ["All", "HIIT", "Strength", "Cardio", "Yoga", "Mobility", "Core"];

export const Workouts = () => {
  const [filter, setFilter] = useState("All");
  const headerRef = useReveal();
  const filtered = filter === "All" ? workouts : workouts.filter((w) => w.category === filter);

  return (
    <section id="workouts" data-testid="workouts-section" className="relative py-20 sm:py-24">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <div ref={headerRef} className="reveal flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-10">
          <div>
            <div className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/5 px-3 py-1 text-xs uppercase tracking-widest text-white/70">
              <Flame className="h-3.5 w-3.5 text-neon-orange" />
              Workouts
            </div>
            <h2 className="mt-4 font-display text-4xl sm:text-5xl tracking-tight text-white">
              PICK YOUR POISON.
              <br />
              <span className="text-neon-lime">WE&rsquo;LL HANDLE THE REST.</span>
            </h2>
            <p className="mt-3 text-white/65 max-w-xl">
              Adaptive plans across 6 disciplines. Every session scales with you — no more guesswork.
            </p>
          </div>

          <Tabs value={filter} onValueChange={setFilter} data-testid="workout-filter-tabs" className="w-full md:w-auto">
            <TabsList className="bg-white/5 border border-white/10 p-1 rounded-xl h-auto flex-wrap gap-1 justify-start">
              {categories.map((c) => (
                <TabsTrigger
                  key={c}
                  value={c}
                  data-testid={`workout-filter-tab-${c.toLowerCase()}`}
                  className="px-3 py-1.5 text-xs font-medium rounded-lg text-white/70 data-[state=active]:bg-neon-lime data-[state=active]:text-ink-0 data-[state=active]:shadow-glow-lime"
                >
                  {c}
                </TabsTrigger>
              ))}
            </TabsList>
          </Tabs>
        </div>

        <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {filtered.map((w) => (
            <WorkoutCard key={w.id} w={w} />
          ))}
        </div>
      </div>
    </section>
  );
};

const WorkoutCard = ({ w }) => {
  const ref = useReveal();
  const Icon = iconForCat[w.category] || Dumbbell;
  const accentColor =
    w.accent === "lime"
      ? "bg-neon-lime/10 border-neon-lime/30 text-neon-lime"
      : w.accent === "cyan"
      ? "bg-neon-cyan/10 border-neon-cyan/30 text-neon-cyan"
      : "bg-neon-orange/10 border-neon-orange/30 text-neon-orange";
  const borderHover =
    w.accent === "lime"
      ? "hover:border-neon-lime/40 hover:shadow-glow-lime"
      : w.accent === "cyan"
      ? "hover:border-neon-cyan/40 hover:shadow-glow-cyan"
      : "hover:border-neon-orange/40 hover:shadow-glow-orange";
  const barGradient =
    w.accent === "lime"
      ? "from-neon-lime to-neon-cyan"
      : w.accent === "cyan"
      ? "from-neon-cyan to-[#2EE59D]"
      : "from-neon-orange to-[#FFB347]";

  return (
    <Card
      ref={ref}
      data-testid="workout-preview-card"
      className={`reveal card-lift bg-white/[0.03] border-white/10 rounded-2xl overflow-hidden ${borderHover}`}
    >
      <CardContent className="p-6">
        <div className="flex items-start justify-between">
          <div className={`inline-flex h-11 w-11 items-center justify-center rounded-xl border ${accentColor}`}>
            <Icon className="h-5 w-5" />
          </div>
          <Badge className={`border rounded-full px-2.5 py-1 text-[11px] font-medium ${diffBadge[w.difficulty]}`}>
            {w.difficulty}
          </Badge>
        </div>

        <h3 className="mt-5 text-white font-semibold text-lg leading-tight">{w.name}</h3>
        <p className="mt-1.5 text-sm text-white/60 leading-relaxed">{w.copy}</p>

        <div className="mt-4 flex flex-wrap gap-2">
          <span className="inline-flex items-center gap-1.5 rounded-full border border-white/10 bg-white/5 px-2.5 py-1 text-xs text-white/80">
            <Timer className="h-3.5 w-3.5" /> {w.duration} min
          </span>
          <span className="inline-flex items-center gap-1.5 rounded-full border border-white/10 bg-white/5 px-2.5 py-1 text-xs text-white/80">
            <Flame className="h-3.5 w-3.5" /> {w.kcal} kcal
          </span>
          <span className="inline-flex items-center gap-1.5 rounded-full border border-white/10 bg-white/5 px-2.5 py-1 text-xs text-white/80">
            {w.equipment}
          </span>
        </div>

        <div className="mt-5">
          <div className="flex items-center justify-between text-xs text-white/55 mb-1.5">
            <span>Program progress</span>
            <span className="font-mono-num text-white/80">{w.progress}%</span>
          </div>
          <div className="h-1.5 w-full rounded-full bg-white/10 overflow-hidden">
            <div
              className={`h-full rounded-full bg-gradient-to-r ${barGradient}`}
              style={{ width: `${w.progress}%` }}
            />
          </div>
        </div>

        <div className="mt-6 flex items-center justify-between">
          <span className="text-xs uppercase tracking-widest text-white/45">{w.category}</span>
          <Button
            variant="ghost"
            data-testid={`workout-card-cta-${w.id}`}
            className="btn-lift h-9 px-3 text-sm text-white hover:text-neon-lime hover:bg-white/5"
          >
            Preview
            <ArrowRight className="ml-1.5 h-4 w-4" />
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};
